import type { SQL } from "drizzle-orm";
import type { AnySQLiteColumn } from "drizzle-orm/sqlite-core";
import type { db } from "../index";
import type {
  insertMessagesSchema,
  patchMessagesSchema,
  selectMessagesSchema,
} from "../schema/message";
import type {
  insertUsersSchema,
  patchUsersSchema,
  selectUsersSchema,
} from "../schema/users";

import { createId } from "@paralleldrive/cuid2";
import { sql } from "drizzle-orm";
import { blob, int, sqliteTableCreator, text } from "drizzle-orm/sqlite-core";
import { z } from "zod";

/**
 * @see https://orm.drizzle.team/docs/goodies#multi-project-schema
 */
export const dbTableNameResolver = (name: string) => `${name}`;
export const createTable = sqliteTableCreator(dbTableNameResolver);

export const now = sql`(unixepoch())`;
export const string = text;
export const number = (field: string) => int(field, { mode: "number" });
export const boolean = (field: string) => int(field, { mode: "boolean" });
export const json = (field: string) => text(field, { mode: "json" });
export const bigint = (field: string) => blob(field, { mode: "bigint" });
export const timestamp = (field: string) => int(field, { mode: "timestamp" });
export const cuid = (field: string) => string(field);

export const id = cuid("id").primaryKey().notNull().$defaultFn(createId);
export const createdAt = timestamp("created_at").default(now).notNull();
export const updatedAt = timestamp("updated_at").$onUpdate(() => new Date());
export const deletedAt = timestamp("deleted_at")
  .$default(() => sql`null`)
  .$type<Date | null>();

export const lower = (email: AnySQLiteColumn): SQL => sql`lower(${email})`;

// NOTE: since createdAt resolved at DB, updatedAt is at ORM level
// updatedAt might be greater than createdAt in subsequent inserts

export type Database = typeof db;
export type InsertUserSchema = z.infer<typeof insertUsersSchema>;
export type SelectUserSchema = z.infer<typeof selectUsersSchema>;
export type PatchUsersSchema = z.infer<typeof patchUsersSchema>;
export type SelectMessagesSchema = z.infer<typeof selectMessagesSchema>;
export type PatchMessagesSchema = z.infer<typeof patchMessagesSchema>;
export type InsertMessageSchema = z.infer<typeof insertMessagesSchema>;

export const backenMessageUpdateSchema = z.object({
  messagesId: z.array(z.string()),
  messagesStatus: z.enum(["sent", "delivered", "read"]),
  updatedAt: z.string(),
});

export type BackendMessageUpdateSchema = z.infer<
  typeof backenMessageUpdateSchema
>;
